import axios from 'axios';

const trimTrailingSlash = (value = '') => value.replace(/\/+$/, '');
const stripBearerPrefix = (value = '') => value.replace(/^Bearer\s+/i, '');

export const API_BASE_URL = trimTrailingSlash('https://api.condutor.online/gest');
export const API_SANCTUM_BASE_URL = trimTrailingSlash('https://api.condutor.online');
export const AUTH_TOKEN_STORAGE_KEY = 'fleetcontrol_auth_token';
const AUTH_USER_STORAGE_KEY = 'fleetcontrol_auth';

const clearStoredAuthSession = () => {
    try {
        localStorage.removeItem(AUTH_USER_STORAGE_KEY);
    } catch { /* ignore */
    }
    try {
        localStorage.removeItem(AUTH_TOKEN_STORAGE_KEY);
    } catch { /* ignore */
    }
    delete api.defaults.headers.common.Authorization;
};

const redirectToLogin = () => {
    try {
        if (window.location.pathname !== '/login') {
            window.location.replace('/login');
        }
    } catch { /* ignore */
    }
};

export const getAuthToken = () => {
    try {
        return stripBearerPrefix(localStorage.getItem(AUTH_TOKEN_STORAGE_KEY) || '');
    } catch {
        return '';
    }
};

export const setAuthToken = (token) => {
    const cleanToken = stripBearerPrefix(token || '');

    if (cleanToken) {
        try {
            localStorage.setItem(AUTH_TOKEN_STORAGE_KEY, cleanToken);
        } catch { /* ignore */
        }
        api.defaults.headers.common.Authorization = `Bearer ${cleanToken}`;
        return;
    }

    try {
        localStorage.removeItem(AUTH_TOKEN_STORAGE_KEY);
    } catch { /* ignore */
    }
    delete api.defaults.headers.common.Authorization;
};

export const clearAuthToken = () => {
    setAuthToken('');
};

export const api = axios.create({
    baseURL: API_BASE_URL,
    withCredentials: true,
    withXSRFToken: true,
    headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
    },
});

api.interceptors.request.use((config) => {
    const token = getAuthToken();
    if (token && !config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

api.interceptors.response.use(
    response => response,
    error => {
        const status = error?.response?.status;
        const hasStoredSession = Boolean(getAuthToken());

        if (hasStoredSession && (status === 401 || status === 419)) {
            clearStoredAuthSession();
            redirectToLogin();
        }

        return Promise.reject(error);
    }
);

export const authEndpoints = {
    csrf: API_SANCTUM_BASE_URL + '/sanctum/csrf-cookie',
    login: API_BASE_URL + '/login',
    logout: API_BASE_URL + '/logout',
    user: API_BASE_URL + '/me',
    password: API_BASE_URL + '/password/email',
    resetPassword: API_BASE_URL + '/password/reset',
    changePassword: API_BASE_URL + '/password/update',
    emailVerificationNotification: API_BASE_URL + '/email/verification-link',
    publicEmailVerificationNotification: API_BASE_URL + '/email/verification-link',
    verifyEmail: (id, hash, queryString = '') => (
        API_BASE_URL + `/email/verify/${id}/${hash}${queryString}`
    ),
    verifiedEmailPassword: (id, hash, queryString = '') => (
        API_BASE_URL + `/email/verify/${id}/${hash}/password${queryString}`
    ),
};

export const roleEndpoints = {
    list: API_BASE_URL + '/roles',
};

export const permissionEndpoints = {
    list: API_BASE_URL + '/permissions',
};

export const activityLogEndpoints = {
    list: API_BASE_URL + '/activity-logs',
    legacyList: API_BASE_URL + '/activity-log',
};

export const reportEndpoints = {
    list: API_BASE_URL + '/reports',
};

export const notificationEndpoints = {
    list: API_BASE_URL + '/notifications',
};

export const searchEndpoints = {
    global: API_BASE_URL + '/search',
};

export const settingsEndpoints = {
    list: API_BASE_URL + '/settings',
    update: API_BASE_URL + '/settings/update',
};

export const companyEndpoints = {
    current: API_BASE_URL + '/company',
    list: API_BASE_URL + '/companies',
    update: API_BASE_URL + '/companies/update',
};

export const userEndpoints = {
    list: API_BASE_URL + '/users',
    create: API_BASE_URL + '/users/store',
    update: API_BASE_URL + '/users/update',
    resendInvite: API_BASE_URL + '/users/resend-invite',
    toggleStatus: API_BASE_URL + '/users/toggle-status',
    delete: API_BASE_URL + '/users/delete',
};

export const massAlertEndpoints = {
    list: API_BASE_URL + '/mass-alerts',
    create: API_BASE_URL + '/mass-alerts/store',
    update: API_BASE_URL + '/mass-alerts/update',
    delete: API_BASE_URL + '/mass-alerts/delete',
};

export const massAlertTemplateEndpoints = {
    list: API_BASE_URL + '/mass-alert-templates',
    create: API_BASE_URL + '/mass-alert-templates/store',
    update: API_BASE_URL + '/mass-alert-templates/update',
    delete: API_BASE_URL + '/mass-alert-templates/delete',
};

export const objectTypeEndpoints = {
    list: API_BASE_URL + '/object-types',
    tagRequestConfig: (objectTypeId) => API_BASE_URL + `/object-types/${objectTypeId}/tag-pages`,
};

export const resourceEndpoints = {
    list: API_BASE_URL + '/resources',
    create: API_BASE_URL + '/resources/store',
    update: API_BASE_URL + '/resources/update',
    delete: API_BASE_URL + '/resources/delete',
    uploadImage: API_BASE_URL + '/resources/upload-image',
    assignDriver: API_BASE_URL + '/resources/assign-manager',
    removeDriver: API_BASE_URL + '/resources/remove-manager',
    swapDrivers: API_BASE_URL + '/resources/swap-manager',
    assignContactRuleGroups: API_BASE_URL + '/resources/assign-contact-rule-groups',
};

export const userResourceAssignmentEndpoints = {
    list: API_BASE_URL + '/user-resource-assignments',
    attach: API_BASE_URL + '/user-resource-assignments/attach',
    detach: API_BASE_URL + '/user-resource-assignments/detach',
};

export const departmentEndpoints = {
    list: API_BASE_URL + '/departments',
    create: API_BASE_URL + '/departments/store',
    update: API_BASE_URL + '/departments/update',
    delete: API_BASE_URL + '/departments/delete',
};

export const occurrencePriorityEndpoints = {
    list: API_BASE_URL + '/occurrence-priorities',
    create: API_BASE_URL + '/occurrence-priorities/store',
    update: API_BASE_URL + '/occurrence-priorities/update',
    delete: API_BASE_URL + '/occurrence-priorities/delete',
};

export const occurrenceTypeEndpoints = {
    list: API_BASE_URL + '/occurrence-types',
    listCompact: API_BASE_URL + '/occurrence-types?view=list',
    detail: (id) => API_BASE_URL + `/occurrence-types/${id}`,
    create: API_BASE_URL + '/occurrence-types/store',
    update: API_BASE_URL + '/occurrence-types/update',
    delete: API_BASE_URL + '/occurrence-types/delete',
};

export const occurrenceStatusEndpoints = {
    list: API_BASE_URL + '/occurrence-statuses',
};

export const occurrenceEndpoints = {
    list: API_BASE_URL + '/occurrences',
    create: API_BASE_URL + '/occurrences/store',
    update: API_BASE_URL + '/occurrences/update',
    delete: API_BASE_URL + '/occurrences/delete',
};

export const ticketEndpoints = {
    list: API_BASE_URL + '/tickets',
    create: API_BASE_URL + '/tickets/store',
    update: API_BASE_URL + '/tickets/update',
    delete: API_BASE_URL + '/tickets/delete',
    occurrenceAttachmentDownload: (id) => API_BASE_URL + `/tickets/occurrence-attachments/${id}/download`,
};

export const driverContactRuleEndpoints = {
    list: API_BASE_URL + '/manager-contact-rules',
    create: API_BASE_URL + '/manager-contact-rules/store',
    update: API_BASE_URL + '/manager-contact-rules/update',
    delete: API_BASE_URL + '/manager-contact-rules/delete',
};

export const resourceContactRuleGroupEndpoints = {
    list: API_BASE_URL + '/resource-contact-rule-groups',
    create: API_BASE_URL + '/resource-contact-rule-groups/store',
    update: API_BASE_URL + '/resource-contact-rule-groups/update',
    delete: API_BASE_URL + '/resource-contact-rule-groups/delete',
};

export const tagRequestEndpoints = {
    create: API_BASE_URL + '/tag-requests',
};

export const tagEndpoints = {
    list: API_BASE_URL + '/tags',
    detail: (id) => API_BASE_URL + `/tags/${encodeURIComponent(id)}`,
    assign: API_BASE_URL + '/tags/assign-resource',
    removeAssignment: API_BASE_URL + '/tags/remove-resource',
};

export const tagBatchEndpoints = {
    list: API_BASE_URL + '/tag-batches',
};

export const externalContactEndpoints = {
    list: API_BASE_URL + '/external-contacts',
    create: API_BASE_URL + '/external-contacts/store',
    update: API_BASE_URL + '/external-contacts/update',
    delete: API_BASE_URL + '/external-contacts/delete',
};

export const externalContactTypeEndpoints = {
    list: API_BASE_URL + '/external-contact-types',
    create: API_BASE_URL + '/external-contact-types/store',
    update: API_BASE_URL + '/external-contact-types/update',
    delete: API_BASE_URL + '/external-contact-types/delete',
};

export const resourceStatusEndpoints = {
    list: API_BASE_URL + '/resource-statuses',
    create: API_BASE_URL + '/resource-statuses/store',
    update: API_BASE_URL + '/resource-statuses/update',
    delete: API_BASE_URL + '/resource-statuses/delete',
};

export const tagDeactivationReasonEndpoints = {
    list: API_BASE_URL + '/tag-deactivation-reasons',
    create: API_BASE_URL + '/tag-deactivation-reasons/store',
    update: API_BASE_URL + '/tag-deactivation-reasons/update',
    delete: API_BASE_URL + '/tag-deactivation-reasons/delete',
};

export const objectColorEndpoints = {
    list: API_BASE_URL + '/object-colors',
};

export const objectIconEndpoints = {
    list: API_BASE_URL + '/object-icons',
};

const collectionPayload = (payload, key) => {
    const source = payload?.data ?? payload;
    if (Array.isArray(source)) return source;
    if (Array.isArray(source?.data)) return source.data;
    if (Array.isArray(source?.[key])) return source[key];
    return [];
};

const unwrapAttributes = (source = {}) => {
    const value = source?.data && typeof source.data === 'object' && !Array.isArray(source.data)
        ? source.data
        : source;
    return value?.attributes && typeof value.attributes === 'object' && !Array.isArray(value.attributes)
        ? {...value, ...value.attributes}
        : value;
};

const objectTypeIdFrom = (...sources) => {
    for (const source of sources) {
        const item = unwrapAttributes(source || {});
        const id = item?.object_type_id ?? item?.company_object_type_id ?? item?.gest_object_type_id ?? item?.type_id ?? item?.id;
        if (id !== undefined && id !== null && id !== '') return id;
    }
    return null;
};

const normalizeTagBatch = (source) => {
    const batch = unwrapAttributes(source);
    const objectType = unwrapAttributes(batch.object_type ?? {});
    const id = source?.id ?? batch?.id ?? batch?.batch_id ?? batch?.tag_batch_id ?? batch?.gest_tag_batch_id ?? batch?.gest_tag_batches_id;
    const objectTypeId = objectTypeIdFrom(
        {
            object_type_id: batch?.object_type_id,
            company_object_type_id: batch?.company_object_type_id,
            gest_object_type_id: batch?.gest_object_type_id,
            type_id: batch?.type_id,
        },
        objectType
    );
    return {
        ...batch,
        id,
        code: batch?.code ?? batch?.batch_code ?? batch?.reference ?? (id ? `Lote ${id}` : ''),
        objectTypeId,
        objectTypeName: objectType?.name ?? objectType?.label ?? objectType?.title ?? batch?.object_type_name ?? '',
        tagsNo: batch?.tags_no ?? batch?.tags_count ?? batch?.quantity ?? 0,
        tagsPrefix: batch?.tags_prefix ?? '',
        tagsSerialFirstNo: batch?.tags_serial_first_no ?? null,
        status: batch?.status ?? 0,
        deliveredAt: batch?.deliveredAt ?? batch?.delivered_at ?? batch?.delivered_date ?? null,
    };
};

export const fetchTagBatches = async () => {
    const response = await api.get(tagBatchEndpoints.list);
    return collectionPayload(response.data, 'tag_batches')
        .map(normalizeTagBatch)
        .filter(batch => batch.id);
};

export const getApiErrorMessage = (error, fallback = 'Ocorreu um erro. Tente novamente.') => {
    const data = error?.response?.data;

    if (data?.message) return data.message;

    if (data?.errors) {
        const firstError = Object.values(data.errors).flat().find(Boolean);
        if (firstError) return firstError;
    }

    if (error?.request && !error?.response) {
        return 'Nao foi possível contactar o servidor.';
    }

    return fallback;
};
